'use client'

import Link from 'next/link'
import { ToastAction } from '@/components/ui/toast'
import { toast } from '@/components/ui/use-toast'
import { listingSuccessTarget } from '@/utils/market/listing-success-link'
import type { IsolateTarget } from '@/utils/market/secure-listing-client'
import event from '@/utils/event'
import Wallet from '@/utils/wallet'

type ListingToastInput = {
	title: string
	description: string
	variant?: 'default' | 'destructive' | 'success' | 'warning'
	slug?: string | null
	inscriptionIds?: Array<string | null | undefined>
	collectionSlugs?: Array<string | null | undefined>
	count: number
	/** Item to move onto its own UTXO; turns the action into "Send to yourself". */
	isolate?: IsolateTarget | null
}

/**
 * Open the Send tab for one item, addressed to the seller's own inscription
 * address and built with postage, so the API splits the shared UTXO by sat
 * offset and the item lands alone on a fresh, listable outpoint.
 */
export async function openIsolateSend(target: IsolateTarget) {
	const { default: CollectionDetail } = await import(
		'@/components/search-input/pages/collection-detail'
	)
	// Remember why the seller is splitting, so the app can offer the
	// protected listing again the moment the split confirms.
	void import('@/utils/market/relist-after-split').then((m) =>
		m.rememberRelistAfterSplit(target.inscriptionId, target.slug ?? null)
	)
	event.emit('command', {
		command: {
			featured: false,
			component: CollectionDetail,
			name: 'Send to yourself',
			heading: 'Send to yourself',
			args: {
				slug: target.slug || 'uncategorized',
				tab: 'send',
				inscriptionId: target.inscriptionId,
				to: Wallet.getTaprootAddress(),
				isolate: true
			}
		}
	})
}

/**
 * Listing-success toast with a View listing action.
 * The link is rendered by the root toaster, so it still works after the
 * list modal closes. Clicking it also dismisses the toast.
 *
 * When an item was refused because of the UTXO it sits on (shared with other
 * inscriptions, too little postage, a burned outpoint), the action is
 * "Send to yourself" and opens the isolating self-send instead — the fix is
 * one click away rather than buried in the description.
 */
export function toastListingSuccess(input: ListingToastInput) {
	const isolate = input.isolate ?? null
	const target = isolate ? null : listingSuccessTarget(input)
	let dismiss = () => {}
	const handle = toast({
		title: input.title,
		description: input.description,
		...(input.variant ? { variant: input.variant } : {}),
		...(isolate
			? {
					action: (
						<ToastAction
							altText="Send to yourself"
							onClick={() => {
								dismiss()
								void openIsolateSend(isolate)
							}}
						>
							Send to yourself
						</ToastAction>
					)
			  }
			: {}),
		...(target
			? {
					action: (
						<ToastAction altText={target.label} asChild>
							<Link href={target.href} onClick={() => dismiss()}>
								{target.label}
							</Link>
						</ToastAction>
					)
			  }
			: {})
	})
	dismiss = handle.dismiss
	return handle
}
